import { vaultFilesApi } from "@api";
import { queriesVaultFiles } from "@query";
import {
  toWSDateString,
  WSImage,
  WSInfoBox,
  WSList
} from "@wingspanhq/fe-component-library";
import { WSEmptyStateProps } from "@wingspanhq/fe-component-library/dist/lib/components/WSEmptyState/WSEmptyState";
import {
  BiometricIdentityVerificationRequest,
  BiometricIdentityVerificationRequestStatus
} from "@wingspanhq/wingspan-api-sdk";
import React, { useEffect } from "react";

import emptyDocument from "../../../../../../../src/assets/images/documents/documents.svg";
import { mapStatusesToCardDetails } from "../../../../helpers/mapStatusesToCardDetails";
import { RequirementTableItemData } from "../../../../types";

export const Image: React.FC<{
  fileId: string;
  isLastOne: boolean;
}> = ({ fileId, isLastOne }) => {
  const [image, setImage] = React.useState<string | null>(null);

  // Only the last image carries the "Uploaded on" line, so the metadata read is
  // scoped to it — the bytes below already carry their own content type.
  const queryVaultFile = queriesVaultFiles.getVaultFile.useQuery(
    { fileId },
    { enabled: isLastOne }
  );
  const fileUploadedAt = queryVaultFile.data?.events?.createdAt;

  useEffect(() => {
    let objectUrl: string | undefined;
    let isCancelled = false;

    (async () => {
      try {
        // The content read is generated `void` (the endpoint answers a
        // redirect), so the blob comes off the raw response — the same escape
        // hatch the shared-file and payStatement downloads use. The blob carries the
        // response's content type, so no separate mime-type read is needed.
        const response = await vaultFilesApi.getVaultFileContentRaw({ fileId });
        const url = URL.createObjectURL(await response.raw.blob());

        if (isCancelled) {
          URL.revokeObjectURL(url);
          return;
        }

        objectUrl = url;
        setImage(url);
      } catch {
        // An id the vault does not resolve renders as no image rather than an
        // unhandled rejection — see the IDV § of docs/known-gaps.md.
      }
    })();

    return () => {
      isCancelled = true;

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileId]);

  if (image) {
    return (
      <>
        <WSImage
          colorBackground="gray200"
          placeholder={<WSImage placeholder={null} src={emptyDocument} />}
          src={image}
        />

        {isLastOne && fileUploadedAt ? (
          <WSInfoBox>Uploaded on {toWSDateString(fileUploadedAt)}</WSInfoBox>
        ) : null}
      </>
    );
  }

  return null;
};

export const payeeIdentityVerificationRenderCardDescription = (
  requirement: RequirementTableItemData,
  identityVerification?: BiometricIdentityVerificationRequest
) => {
  // The request's own `resultFiles` are the payer-owned copies of the images —
  // the same set v1 returned on the request (the verification's copies carry
  // different ids and are payee-only).
  const images = identityVerification?.resultFiles?.filter(
    file => file.imageType && file.fileId
  );

  if (images?.length) {
    return (
      <WSList gap="L">
        {images.map((image, index, all) => (
          <Image
            key={image.fileId}
            fileId={image.fileId!}
            isLastOne={index === all.length - 1}
          />
        ))}
      </WSList>
    );
  }

  const pendingDescription: WSEmptyStateProps = {
    type: "people",
    title: "ID verification is pending",
    description:
      "Waiting for the contractor to start the ID verification process."
  };

  const failedDescription: WSEmptyStateProps = {
    type: "people",
    title: "Something went wrong with ID verification",
    description: "There was an issue with ID verification."
  };

  const descriptions: Record<
    BiometricIdentityVerificationRequestStatus,
    WSEmptyStateProps
  > = {
    [BiometricIdentityVerificationRequestStatus.PENDING]: pendingDescription,
    [BiometricIdentityVerificationRequestStatus.COMPLETE]: {
      type: "done",
      title: "ID verification is complete",
      description:
        "ID verification is complete. The results will be shown here soon."
    },
    [BiometricIdentityVerificationRequestStatus.FAILED]: failedDescription
  };

  return mapStatusesToCardDetails({
    requirement,
    documentStatus:
      identityVerification?.status ||
      BiometricIdentityVerificationRequestStatus.PENDING,
    documentStatuses: descriptions,
    pendingApprovalStatus: {
      type: "people",
      title: "ID verification was submitted for review",
      description: "Contractor submitted ID verification for your approval."
    },
    approvedStatus: {
      type: "people",
      title: "ID verification was approved",
      description:
        "ID verification and biometrics matching was approved. No further action is required."
    },
    rejectedStatus: {
      type: "people",
      title: "ID verification was rejected",
      description:
        "ID verification was rejected by you. No further action is required."
    }
  });
};
